import { Backdrop, Grid } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AryAlertTypes } from 'framework/base/components/AryAlert';
import useAryAlert from 'framework/base/hooks/useAryAlert';
import useLayoutTypeContext from 'framework/base/hooks/useLayoutTypeContext';
import useTemplateParsing from 'framework/base/hooks/useTemplateParsing';
import {
  compressFileForProfile,
  compressFiles,
  dataURLtoFile,
  getFileNameFromFileId
} from 'framework/base/utils/Utils';
import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import { useDropzone } from 'react-dropzone';
import useFrameworkTranslation from 'framework/language/useFrameworkTranslation';
import Webcam from 'react-webcam';
import AryLoadingButton from '../AryLoadingButton';
import AryModal from '../AryModal';
import EmptyDropZone from './components/EmptyDropZone';
import HasContentDropZone from './components/HasContentDropZone';
import ShowImgPreview from './components/ShowImgPreview';
import {
  acceptStyle,
  baseStyle,
  rejectStyle
} from './components/StyledComponents';

const MAX_FILE_SIZE = 3145728;

const AryFileUploader = forwardRef(
  (
    {
      fileIds: propsFileIds = [],
      removedFileIds,
      handleRemovedFileIds,
      files = [],
      onChange,
      readOnly = false,
      handleSavedFileIds,
      handleSetIsImgPreviewShow,
      uploaderWidth,
      hasWebcam,
      ...others
    },
    ref
  ) => {
    const theme = useTheme();
    const layoutType = useLayoutTypeContext();
    const parseDescription = useTemplateParsing();
    const webcamRef = useRef(null);
    const { t } = useFrameworkTranslation();

    const [AryAlert, showAlert] = useAryAlert();
    const [previewImg, setPreviewImg] = useState(null);
    const [prevFiles, setPrevFiles] = useState(propsFileIds || []);
    const [previews, setPreviews] = useState([]);
    const [isHover, setIsHover] = useState(false);
    const [newImageNumber, setNewImageNumber] = useState(1);
    const [disableDropzone, setDisabledDropzone] = useState(false);
    const [openWebcam, setOpenWebcam] = useState(false);
    const capture = useCallback(() => {
      const imageSrc = webcamRef.current.getScreenshot();
      const file = dataURLtoFile(imageSrc, `image-${+new Date()}.jpg`);
      if (file.size > MAX_FILE_SIZE) {
        showAlert(
          AryAlertTypes.ERROR,
          parseDescription('Client-FileSize-Exceed', [
            {
              hook: 'name',
              value: file.name
            }
          ])
        );
        return;
      }
      compressFileForProfile(file).then((compressedFile) => {
        onChange(files.concat(compressedFile));
      });
      setOpenWebcam(false);
    }, [webcamRef, files]);

    useEffect(() => {
      showFiles(files);
    }, [files]);

    const handleShowPreview = (newPreview) => {
      setPreviewImg(newPreview);
      if (newPreview) {
        handleSetIsImgPreviewShow(true);
      } else {
        handleSetIsImgPreviewShow(false);
      }
    };
    const showImgWidthFileIds = () => {
      setPrevFiles(propsFileIds);
      const newFiles = propsFileIds?.map((fileId) => {
        const isFileDownloadedBefore = previews.find(
          (prev) => prev.fileId === fileId && prev.downloaded
        );
        if (!isFileDownloadedBefore) {
          return {
            fileId,
            imgSrc:
              getFileNameFromFileId(fileId)
                .split('.')
                .map((elem) => elem.toLowerCase())
                .pop() === 'pdf'
                ? 'pdf'
                : getFileNameFromFileId(fileId)
                    .split('.')
                    .map((elem) => elem.toLowerCase())
                    .pop() === 'doc' ||
                  getFileNameFromFileId(fileId)
                    .split('.')
                    .map((elem) => elem.toLowerCase())
                    .pop() === 'docx'
                ? 'docx'
                : getFileNameFromFileId(fileId)
                    .split('.')
                    .map((elem) => elem.toLowerCase())
                    .pop() === 'xls' ||
                  getFileNameFromFileId(fileId)
                    .split('.')
                    .map((elem) => elem.toLowerCase())
                    .pop() === 'xlsx'
                ? 'xlsx'
                : undefined,
            name: getFileNameFromFileId(fileId),
            downloaded: false
          };
        }
        return isFileDownloadedBefore;
      });
      return newFiles;
    };
    const showFiles = (items) => {
      let temp;
      if (propsFileIds?.length) {
        temp = showImgWidthFileIds();
      }
      let newPrev;
      if (files?.length) {
        newPrev = items.map((compressedFile) => ({
          name: compressedFile.name,
          imgSrc:
            compressedFile.type === 'application/pdf'
              ? 'pdf'
              : compressedFile.type === 'application/msword' ||
                compressedFile.type ===
                  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
              ? 'docx'
              : compressedFile.type === 'application/vnd.ms-excel' ||
                compressedFile.type ===
                  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
              ? 'xlsx'
              : URL.createObjectURL(compressedFile),
          downloaded: true
        }));
      }
      if (propsFileIds?.length) {
        setPreviews(newPrev ? temp.concat(newPrev) : temp);
      } else {
        setPreviews(newPrev);
      }
    };
    function updatePreviewsAfterFileDownloaded(currentFile, newFileData) {
      const downloadedFileIndex = previews.findIndex(
        (obj) => obj.fileId === currentFile.fileId
      );

      const downloadedFile =
        currentFile.imgSrc === 'pdf' ||
        currentFile.imgSrc === 'xlsx' ||
        currentFile.imgSrc === 'docx'
          ? currentFile
          : new File([newFileData], previews[downloadedFileIndex].name, {
              type: newFileData.type
            });
      setPreviews((prevPreviews) => {
        const newPreviews = prevPreviews.slice();
        newPreviews[downloadedFileIndex] = {
          ...previews[downloadedFileIndex],
          imgSrc:
            currentFile.imgSrc === 'pdf' ||
            currentFile.imgSrc === 'docx' ||
            currentFile.imgSrc === 'xlsx'
              ? currentFile.imgSrc
              : URL.createObjectURL(downloadedFile),
          downloaded:
            newFileData.type !== 'application/pdf' &&
            newFileData.type !== 'application/msword' &&
            newFileData.type !==
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document' &&
            newFileData.type !== 'application/vnd.ms-excel' &&
            newFileData.type !==
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        };
        return newPreviews;
      });
    }
    const onDrop = useCallback(
      (acceptedFiles) => {
        const remainingFiles = acceptedFiles.filter((file) => {
          if (file.size > MAX_FILE_SIZE) {
            showAlert(
              AryAlertTypes.ERROR,
              parseDescription('Client-FileSize-Exceed', [
                {
                  hook: 'name',
                  value: file.name
                }
              ])
            );
            return false;
          }
          return true;
        });
        compressFiles(remainingFiles).then((compressedFiles) => {
          const existedFileNames = files
            .map((obj) => obj.name)
            .concat(prevFiles?.map((fileId) => getFileNameFromFileId(fileId)));
          const noExistedFiles = compressedFiles.filter((obj) => {
            if (
              obj.name !== 'image.png' &&
              existedFileNames.indexOf(obj.name) >= 0
            ) {
              showAlert(
                AryAlertTypes.ERROR,
                parseDescription('Client-FileName-Exists', [
                  {
                    hook: 'name',
                    value: obj.name
                  }
                ])
              );
            }
            return obj.name !== 'image.png'
              ? existedFileNames.indexOf(obj.name) === -1
              : true;
          });
          onChange(files.concat(noExistedFiles));
        });
      },
      [files, previews]
    );
    const { getRootProps, getInputProps, isDragAccept, isDragReject } =
      useDropzone({
        onDrop,
        disabled: !!readOnly || disableDropzone,
        accept:
          'image/png, image/jpeg, image/webp, application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

    const style = useMemo(
      () => ({
        ...baseStyle(theme, !!previews.length, layoutType, isHover, readOnly),
        ...(isDragAccept ? acceptStyle : {}),
        ...(isDragReject ? rejectStyle : {})
      }),
      [isDragAccept, isDragReject, previews, isHover, readOnly]
    );

    const elms = document.getElementsByClassName('MuiModal-root')[0];
    const elms1 = document.getElementById('root');
    const modalElm = elms;

    useEffect(() => {
      const handlePaste = (event) => {
        document.removeEventListener('paste', handlePaste);
        const { items } =
          event.clipboardData || event.originalEvent.clipboardData;
        const newFiles = [];
        Object.keys(items).forEach((i) => {
          const item = items[i];
          if (item.kind === 'file') {
            const blob = item.getAsFile();
            const file = new File(
              [blob],
              blob.name === 'image.png'
                ? `clipboard${newImageNumber}.${blob.name.split('.').pop()}`
                : blob.name,
              {
                type: blob.type
              }
            );
            if (blob.name === 'image.png') {
              setNewImageNumber((prev) => prev + 1);
            }
            newFiles.push(file);
          }
        });
        onDrop(newFiles);
      };

      if (modalElm) {
        modalElm.addEventListener('paste', handlePaste);
      }
      if (elms1) {
        elms1.addEventListener('paste', handlePaste);
      }

      return () => {
        if (modalElm) {
          modalElm.removeEventListener('paste', handlePaste);
        }
        if (elms1) {
          elms1.removeEventListener('paste', handlePaste);
        }
      };
    }, [files, modalElm]);

    useEffect(() => {
      if (files.length === 0 && prevFiles.length === 0) {
        previews.forEach((preview) => URL.revokeObjectURL(preview.imgSrc));
        setPreviews([]);
      }
    }, [files]);

    useEffect(
      () => () => {
        previews.forEach((preview) => URL.revokeObjectURL(preview.imgSrc));
      },
      []
    );
    // to disable or enable dropzone
    useEffect(() => {
      setDisabledDropzone(!!previewImg);
    }, [previewImg]);
    useImperativeHandle(ref, () => ({
      handleInjectNewFile: (file) => {
        onDrop([file]);
      }
    }));
    useEffect(() => {
      let keyPressed = {};
      const openWebcamFn = (event) => {
        keyPressed[event.keyCode] = true;
        if (keyPressed[17] && keyPressed[75]) {
          event.preventDefault();
          setOpenWebcam(true);
        }
      };
      const clearKeyPressed = () => {
        keyPressed = {};
      };
      document.addEventListener('keydown', (event) => {
        if (!hasWebcam) return;
        openWebcamFn(event);
      });
      document.addEventListener('keyup', (event) => {
        if (!hasWebcam) return;
        clearKeyPressed(event);
      });
      return () => {
        document.removeEventListener('keydown', openWebcamFn);
        document.removeEventListener('keyup', clearKeyPressed);
      };
    });
    const handleSetDisabledDropzone = (newState) =>
      setDisabledDropzone(newState);
    return (
      <Grid
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
        style={{
          width: uploaderWidth,
          padding: '2.75rem, 0',
          height: '100%'
        }}
      >
        <Grid
          {...getRootProps({ style })}
          sx={{ height: '100%' }}
          tabIndex={-1}
        >
          <input {...getInputProps()} />
          {!previews.length && <EmptyDropZone hasWebcam={hasWebcam} />}
          {!!previews.length && (
            <HasContentDropZone
              onMouseUp={(event) => {
                event.stopPropagation();
                if (handleRemovedFileIds) {
                  handleRemovedFileIds(propsFileIds);
                }
                setPreviews([]);
                setPrevFiles([]);
                handleSavedFileIds([]);
                onChange([]);
              }}
              readOnly={readOnly}
              previews={previews}
              onShowPreview={handleShowPreview}
              updatePreviewsAfterFileDownloaded={
                updatePreviewsAfterFileDownloaded
              }
              files={files}
              prevFiles={prevFiles}
              propsFileIds={propsFileIds}
              handleRemovedFileIds={handleRemovedFileIds}
              removedFileIds={removedFileIds}
              onChange={onChange}
              setPreviews={setPreviews}
              setPrevFiles={setPrevFiles}
              handleSavedFileIds={handleSavedFileIds}
              handleOpenWebcam={(newValue) => setOpenWebcam(newValue)}
              hasWebcam={hasWebcam}
              handleSetDisabledDropzone={handleSetDisabledDropzone}
            />
          )}
          {!!previewImg && (
            <Backdrop
              sx={{
                background: `linear-gradient(0deg, #566E88, #566E88),
                linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)),
                linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)),
                linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))`,
                zIndex: theme.zIndex.drawer + 1
              }}
              open={previewImg}
            >
              <ShowImgPreview
                image={previewImg}
                onClose={() => {
                  setPreviewImg(null);
                  handleSetIsImgPreviewShow(false);
                }}
                preview={previewImg}
                previews={previews}
                files={files}
                prevFiles={prevFiles}
                propsFileIds={propsFileIds}
                removedFileIds={removedFileIds}
                handleRemovedFileIds={handleRemovedFileIds}
                onChange={onChange}
                setPreviews={setPreviews}
                setPrevFiles={setPrevFiles}
                handleSavedFileIds={handleSavedFileIds}
                closeShowPreviewAfterRemoveFile={handleShowPreview}
                handleSetDisabledDropzone={handleSetDisabledDropzone}
                {...others}
              />
            </Backdrop>
          )}
        </Grid>
        {openWebcam && (
          <AryModal
            open={openWebcam}
            handleClose={() => setOpenWebcam(false)}
            justifyContent="center"
            columnSpacing="0"
            rowSpacing="0"
          >
            <form onSubmit={capture}>
              <Grid item xs={12}>
                <Webcam
                  width="100%"
                  audio={false}
                  mirrored
                  ref={webcamRef}
                  screenshotFormat="image/jpeg"
                />
              </Grid>
              <Grid item xs={12}>
                <AryLoadingButton
                  variant="contained"
                  type="submit"
                  onClick={capture}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      capture();
                    }
                  }}
                  autoFocus
                >
                  {t('TakePicture')}
                </AryLoadingButton>
              </Grid>
            </form>
          </AryModal>
        )}
        {AryAlert}
      </Grid>
    );
  }
);

export default memo(AryFileUploader, (prevProps, nextProps) => {
  const prevProperties = JSON.stringify(prevProps);
  const nextProperties = JSON.stringify(nextProps);
  return prevProperties === nextProperties;
});
